import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { DownloadOutlined, FileOutlined, MailOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import {
  confirmEmailMatchingRecordMatch,
  getEmailMatchingRecord,
  updateEmailMatchingRecordStatus,
} from 'apis/EmailMatchingAPI';
import StyledTable from 'components/Companies/table/StyledTable';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Modal from 'components/lib/Modal';
import { getFormattedDate } from 'helpers/dates';
import { formatPercent } from 'utils/helpers/numbers';

import {
  EMAIL_MATCHING_PAGE_SIZE,
  EMAIL_MATCHING_SOURCE_ICONS,
  EMAIL_MATCHING_SOURCE_LABELS,
  EMAIL_MATCHING_SOURCES,
  EMAIL_MATCHING_STATUS_COLORS,
  EMAIL_MATCHING_STATUS_FILTERS,
  EMAIL_MATCHING_STATUS_LABELS,
} from './constants';
import EmailMatchingEditModalBody from './EmailMatchingEditModalBody';
import EmailMatchingTableActions from './EmailMatchingTableActions';
import { EmailMatchingConfirmMatchPayloadType, EmailMatchingFiltersType, EmailMatchingRecordType } from './types';
import { isMatchedEdited } from './utils';

type Props = {
  items: EmailMatchingRecordType[];
  loading: boolean;
  totalItemsCount: number;
  filters: EmailMatchingFiltersType;
  onPageChange: (page: number) => void;
};

const Container = styled.div`
  position: relative;
  display: flex;
  min-height: 0;
`;

const EmailMatchingStyledTable = styled(StyledTable)`
  &&&&&& {
    .ant-table table {
      table-layout: fixed;
    }

    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      vertical-align: middle;
      padding-right: 20px;
    }

    .ant-table-pagination.ant-pagination {
      padding-bottom: 12px;
    }
  }
`;

const Footer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterLeftActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FooterRightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: calc(100% - 36px);
`;

const ModalTitleTexts = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ModalTitleText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModalSubtitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  margin-top: 6px;
  font-family: var(--font-regular);
  font-size: 13px;
  color: var(--text-secondary);
`;

const ModalSubtitleText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModalSubtitleSeparator = styled.span`
  color: var(--line-gray);
`;

const SourceTag = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border: 1px solid var(--line-gray-bold);
  border-radius: 100px;
  background: var(--feather-blue);
  color: var(--dark-blue);
  font-family: var(--font-bold);
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const OriginCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
`;

const OriginTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const OriginText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OriginSource = styled.span`
  font-size: 10px;
  color: var(--text-secondary);

  &.source-pdf-archive {
    color: var(--error-red);
  }

  &.source-email {
    color: var(--blue-vivid);
  }
`;

const StatusCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatusLabel = styled.span<{ $color?: string }>`
  font-weight: 600;
  color: ${({ $color }) => $color || 'inherit'};
`;

const StatusConfidence = styled.span`
  font-size: 10px;
  color: var(--text-secondary);
  white-space: nowrap;
`;

const SenderCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const SenderEmail = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const CONFIRM_MATCH_ALLOWED_STATUSES: number[] = [
  EMAIL_MATCHING_STATUS_FILTERS.NO_MATCH,
  EMAIL_MATCHING_STATUS_FILTERS.NEEDS_REVIEW,
];

const CONFIDENCE_VISIBLE_STATUSES: number[] = [
  EMAIL_MATCHING_STATUS_FILTERS.NEEDS_REVIEW,
  EMAIL_MATCHING_STATUS_FILTERS.AUTO_MATCHED,
  EMAIL_MATCHING_STATUS_FILTERS.MATCHED,
];

const EmailMatchingTable = ({ items, loading, totalItemsCount, filters, onPageChange }: Props) => {
  const [selectedRecord, setSelectedRecord] = useState<EmailMatchingRecordType | null>(null);
  const [confirmMatchValues, setConfirmMatchValues] = useState<EmailMatchingConfirmMatchPayloadType | null>(null);
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();

  const selectedRecordId = selectedRecord?.id;

  const {
    data: emailMatchingDetail,
    isLoading: isLoadingDetail,
    isFetching: isFetchingDetail,
    isError: isDetailError,
  } = useQuery<EmailMatchingRecordType>({
    queryKey: ['email-matching-record', selectedRecordId],
    queryFn: () => getEmailMatchingRecord(selectedRecordId as number),
    enabled: Boolean(selectedRecordId),
    retry: false,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Failed to load email details.';
      showMessage('error', message);
    },
  });

  const { mutateAsync: updateStatusMutation, isLoading: isUpdatingStatus } = useMutation({
    mutationFn: (status: number) => updateEmailMatchingRecordStatus(selectedRecordId as number, status),
    onSuccess: async () => {
      setSelectedRecord(null);
      await queryClient.invalidateQueries(['email-matching-record', selectedRecordId]);
      await queryClient.invalidateQueries(['email-matching-records']);
      await queryClient.invalidateQueries(['email-matching-tabs-metrics']);
      showMessage('success', 'Record marked as spam.');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Failed to update status.';
      showMessage('error', message);
    },
  });

  const { mutateAsync: confirmMatchMutation, isLoading: isConfirmingMatch } = useMutation({
    mutationFn: (payload: EmailMatchingConfirmMatchPayloadType) =>
      confirmEmailMatchingRecordMatch(selectedRecordId as number, payload),
    onSuccess: async () => {
      setSelectedRecord(null);
      setConfirmMatchValues(null);
      await queryClient.invalidateQueries(['email-matching-record', selectedRecordId]);
      await queryClient.invalidateQueries(['email-matching-records']);
      await queryClient.invalidateQueries(['email-matching-tabs-metrics']);
      await queryClient.invalidateQueries(['email-matching-reviewers']);
      showMessage('success', 'Record confirmed as matched.');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Failed to confirm match.';
      showMessage('error', message);
    },
  });

  const resolveConfirmMatchPayload = (record: EmailMatchingRecordType): EmailMatchingConfirmMatchPayloadType => {
    return {
      matched_community: record.matched_community ?? record.ai_matched_community ?? null,
      sender_type: record.sender_type ?? record.ai_sender_type ?? null,
      delivery_method: record.delivery_method ?? record.ai_delivery_method ?? null,
      communication_types: record.communication_types ?? record.ai_communication_types ?? [],
    };
  };

  const handleOpenEditModal = (record: EmailMatchingRecordType) => {
    queryClient.removeQueries(['email-matching-record', record.id], { exact: true });
    setSelectedRecord(record);
    setConfirmMatchValues(resolveConfirmMatchPayload(record));
  };

  const handleCloseEditModal = () => {
    setSelectedRecord(null);
    setConfirmMatchValues(null);
  };

  const handleDownloadPdf = (record: EmailMatchingRecordType) => {
    if (!record.file_path) {
      showMessage('error', 'PDF file is not available.');
      return;
    }

    window.open(record.file_path, '_blank', 'noopener,noreferrer');
  };

  const handleMarkAsSpam = async () => {
    const status = emailMatchingDetail?.status;

    if (!selectedRecordId || status === EMAIL_MATCHING_STATUS_FILTERS.SPAM || isUpdatingStatus || isConfirmingMatch) {
      return;
    }

    await updateStatusMutation(EMAIL_MATCHING_STATUS_FILTERS.SPAM);
  };

  const handleConfirmMatch = async () => {
    const status = emailMatchingDetail?.status;

    if (
      !selectedRecordId ||
      status === EMAIL_MATCHING_STATUS_FILTERS.MATCHED ||
      !CONFIRM_MATCH_ALLOWED_STATUSES.includes(status as number) ||
      isUpdatingStatus ||
      isConfirmingMatch
    ) {
      return;
    }

    const sourceRecord = emailMatchingDetail;
    if (!sourceRecord) {
      return;
    }

    await confirmMatchMutation(confirmMatchValues ?? resolveConfirmMatchPayload(sourceRecord));
  };

  const handleChange = (pagination: any, _filters: any, _sorter: any, extra: any) => {
    if (extra?.action === 'paginate') {
      onPageChange(pagination.current);
    }
  };

  const columns: any[] = [
    {
      title: 'Origin',
      dataIndex: 'file_name',
      width: '22%',
      render: (_value: string | null, record: EmailMatchingRecordType, index: number) => {
        const isPdfArchive = record.source === EMAIL_MATCHING_SOURCES.PDF_ARCHIVE;
        const originText = isPdfArchive ? record.file_name : record.recipient_email;

        return (
          <OriginCell>
            {isPdfArchive ? <FileOutlined /> : <MailOutlined />}
            <OriginTextColumn>
              <OriginText data-test-id={`email-matching-${index}-origin-value`}>{originText || '-'}</OriginText>
              <OriginSource
                className={isPdfArchive ? 'source-pdf-archive' : 'source-email'}
                data-test-id={`email-matching-${index}-origin-source-value`}
              >
                {EMAIL_MATCHING_SOURCE_LABELS[record.source] || '-'}
              </OriginSource>
            </OriginTextColumn>
          </OriginCell>
        );
      },
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      width: '17%',
      render: (value: string | null, _record: EmailMatchingRecordType, index: number) => (
        <span data-test-id={`email-matching-${index}-subject-value`}>{value || '-'}</span>
      ),
    },
    {
      title: 'Sent At',
      dataIndex: 'sent_at',
      width: '12%',
      render: (value: string | null, _record: EmailMatchingRecordType, index: number) => (
        <span data-test-id={`email-matching-${index}-sent-at-value`}>
          {value ? getFormattedDate(value, 'MMM DD, YYYY hh:mm A') : '-'}
        </span>
      ),
    },
    {
      title: 'Sender',
      dataIndex: 'sender_name',
      width: '17%',
      render: (value: string | null, record: EmailMatchingRecordType, index: number) => {
        const senderName = value || '-';
        const senderEmail = record.sender_email || '-';

        return (
          <SenderCell>
            <span data-test-id={`email-matching-${index}-sender-name-value`}>{senderName}</span>
            <SenderEmail data-test-id={`email-matching-${index}-sender-email-value`}>{senderEmail}</SenderEmail>
          </SenderCell>
        );
      },
    },
    {
      title: 'Community',
      dataIndex: 'matched_community_name',
      width: '16%',
      render: (value: string | null, _record: EmailMatchingRecordType, index: number) => (
        <span data-test-id={`email-matching-${index}-community-value`}>{value || ''}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '12%',
      render: (value: number, record: EmailMatchingRecordType, index: number) => {
        const statusLabel = EMAIL_MATCHING_STATUS_LABELS[value] || '-';
        const isEditedMatch = value === EMAIL_MATCHING_STATUS_FILTERS.MATCHED && isMatchedEdited(record);
        const statusColor = EMAIL_MATCHING_STATUS_COLORS[value];
        const confidence =
          record.ai_community_match_confidence !== null ? formatPercent(record.ai_community_match_confidence) : null;
        const showConfidence = CONFIDENCE_VISIBLE_STATUSES.includes(value) && Boolean(confidence);
        const showConfirmedBy =
          value === EMAIL_MATCHING_STATUS_FILTERS.MATCHED && Boolean(record.confirmed_by_user?.full_name);

        return (
          <StatusCell>
            <StatusLabel $color={statusColor} data-test-id={`email-matching-${index}-status-value`}>
              {isEditedMatch ? `${statusLabel} (Edited)` : statusLabel}
            </StatusLabel>
            {showConfidence && (
              <StatusConfidence data-test-id={`email-matching-${index}-confidence-value`}>
                Confidence: {confidence}
              </StatusConfidence>
            )}
            {showConfirmedBy && (
              <StatusConfidence data-test-id={`email-matching-${index}-confirmed-by-value`}>
                confirmed by {record.confirmed_by_user?.full_name}
              </StatusConfidence>
            )}
          </StatusCell>
        );
      },
    },
    {
      title: '',
      width: '4%',
      render: (_value: any, record: EmailMatchingRecordType) => (
        <EmailMatchingTableActions item={record} onEdit={handleOpenEditModal} onDownloadPdf={handleDownloadPdf} />
      ),
    },
  ];
  const isSpam = (emailMatchingDetail?.status ?? null) === EMAIL_MATCHING_STATUS_FILTERS.SPAM;
  const isMatched = (emailMatchingDetail?.status ?? null) === EMAIL_MATCHING_STATUS_FILTERS.MATCHED;
  const canShowMarkAsSpam = !isMatched;
  const canConfirmMatch = CONFIRM_MATCH_ALLOWED_STATUSES.includes((emailMatchingDetail?.status ?? null) as number);

  return (
    <Container data-test-id="email-matching-table-container">
      <EmailMatchingStyledTable
        data-test-id="email-matching-table"
        columns={columns}
        dataSource={items}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => handleOpenEditModal(record as EmailMatchingRecordType),
        })}
        onChange={handleChange}
        showSorterTooltip={false}
        loading={loading}
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          hideOnSinglePage: true,
          pageSize: EMAIL_MATCHING_PAGE_SIZE,
          current: filters.page,
          total: totalItemsCount,
          showSizeChanger: false,
          showTotal: (count, range) => `${range[0]}-${range[1]} of ${count}`,
        }}
      />

      <Modal
        open={Boolean(selectedRecord)}
        title={
          <ModalTitle>
            <ModalTitleTexts>
              <ModalTitleText data-test-id="email-matching-detail-subject-title">
                {emailMatchingDetail?.subject || 'No subject'}
              </ModalTitleText>
              {emailMatchingDetail && (
                <ModalSubtitle>
                  <SourceTag data-test-id="email-matching-detail-source-tag">
                    {EMAIL_MATCHING_SOURCE_ICONS[emailMatchingDetail.source]}
                    {EMAIL_MATCHING_SOURCE_LABELS[emailMatchingDetail.source]}
                  </SourceTag>
                  {emailMatchingDetail.sent_at && (
                    <>
                      <ModalSubtitleSeparator>·</ModalSubtitleSeparator>
                      <ModalSubtitleText data-test-id="email-matching-detail-sent-at">
                        {getFormattedDate(emailMatchingDetail.sent_at, 'MMM DD, YYYY · hh:mm A')}
                      </ModalSubtitleText>
                    </>
                  )}
                  {emailMatchingDetail.file_name && (
                    <>
                      <ModalSubtitleSeparator>·</ModalSubtitleSeparator>
                      <ModalSubtitleText data-test-id="email-matching-detail-file-name">
                        {emailMatchingDetail.file_name}
                      </ModalSubtitleText>
                    </>
                  )}
                </ModalSubtitle>
              )}
            </ModalTitleTexts>
            <Button
              data-test-id="email-matching-detail-download-pdf-btn"
              icon={<DownloadOutlined />}
              onClick={() => emailMatchingDetail && handleDownloadPdf(emailMatchingDetail)}
              disabled={!emailMatchingDetail?.file_path}
            >
              Download PDF
            </Button>
          </ModalTitle>
        }
        onCancel={handleCloseEditModal}
        footer={
          <Footer>
            <FooterLeftActions>
              {canShowMarkAsSpam && (
                <Button
                  data-test-id="email-matching-detail-mark-as-spam-btn"
                  onClick={handleMarkAsSpam}
                  loading={isUpdatingStatus}
                  disabled={isSpam || isConfirmingMatch}
                >
                  Mark as Spam
                </Button>
              )}
            </FooterLeftActions>
            <FooterRightActions>
              <Button
                data-test-id="email-matching-detail-close-btn"
                onClick={handleCloseEditModal}
                disabled={isUpdatingStatus || isConfirmingMatch}
              >
                Close
              </Button>
              {canConfirmMatch && (
                <Button
                  data-test-id="email-matching-detail-confirm-match-btn"
                  type="primary"
                  onClick={handleConfirmMatch}
                  loading={isConfirmingMatch}
                  disabled={isUpdatingStatus}
                >
                  Confirm Match
                </Button>
              )}
            </FooterRightActions>
          </Footer>
        }
        className="flat"
        destroyOnClose
        width={1280}
      >
        <EmailMatchingEditModalBody
          detail={emailMatchingDetail}
          loading={isLoadingDetail || isFetchingDetail}
          error={isDetailError}
          onConfirmMatchValuesChange={setConfirmMatchValues}
        />
      </Modal>
    </Container>
  );
};

export default EmailMatchingTable;
